// pages/analytics/RerunnerRecovery.jsx — SOUND-414
// Internal analyst dashboard over presentation.mart_rerunner_daily_summary
// (Snowflake MART_RERUNNER_DAILY_SUMMARY -> presentation Postgres, SOUND-412).
// Backend: GET /analytics/rerunner-summary{,/timeseries,/summary,/filters} (SOUND-413).
//
// Grain is (company_id × run_date) — one row per rerunner client per day.
// daily_recovery_percentage is percent (0–100) = successful / attempted.
// Funnel: declined -> filtered_out/already_attempted -> attempted -> successful/failed.
import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import Skeleton from "components/Skeleton";
import { Repeat, CheckCircle, XCircle, Layers } from "lucide-react";

const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);

function cssVar(name, alpha = 1) {
  if (typeof window === "undefined") return "";
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return "";
  const [h, s, l] = raw.split(/\s+/);
  return `hsla(${parseFloat(h)}, ${s}, ${l}, ${alpha})`;
}

const RANGE_PRESETS = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "6mo", days: 182 },
  { label: "1y", days: 365 },
];

function isoDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function buildQS({ startDate, endDate, companyId, limit }) {
  const p = new URLSearchParams();
  if (startDate) p.set("startDate", startDate);
  if (endDate) p.set("endDate", endDate);
  if (companyId) p.set("companyId", companyId);
  if (limit) p.set("limit", String(limit));
  return p.toString();
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="surface rounded-xl ring-1 ring-border p-5">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <ResponsiveContainer width="100%" height={240}>{children}</ResponsiveContainer>
    </div>
  );
}

const tooltipStyle = () => ({
  background: cssVar("--card", 0.98),
  border: `1px solid ${cssVar("--border", 1)}`,
  borderRadius: 6,
  fontSize: 12,
});

function RerunnerRecoveryInner() {
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [rangeDays, setRangeDays] = useState(90);

  const [summary, setSummary] = useState(null);
  const [byCompany, setByCompany] = useState([]);
  const [series, setSeries] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const startDate = useMemo(() => isoDaysAgo(rangeDays), [rangeDays]);
  const endDate = useMemo(() => isoDaysAgo(0), []);
  const auth = useMemo(() => ({ id: user?.id, accessToken: user?.accessToken }), [user]);

  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    (async () => {
      try {
        const res = await makeApiRequest("get", apiRoutes.analyticsRerunnerFilters, auth, effectiveCompanyId);
        setCompanies(res?.data?.companies || []);
      } catch { /* non-fatal */ }
    })();
  }, [user, effectiveCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || !effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      const qs = buildQS({ startDate, endDate, companyId });
      const rowQs = buildQS({ startDate, endDate, companyId, limit: 2000 });
      try {
        const [s, t, r] = await Promise.all([
          makeApiRequest("get", `${apiRoutes.analyticsRerunnerSummary}?${qs}`, auth, effectiveCompanyId),
          makeApiRequest("get", `${apiRoutes.analyticsRerunnerTimeseries}?${qs}`, auth, effectiveCompanyId),
          makeApiRequest("get", `${apiRoutes.analyticsRerunner}?${rowQs}`, auth, effectiveCompanyId),
        ]);
        if (cancelled) return;
        setSummary(s?.data?.totals || null);
        setByCompany(s?.data?.byCompany || []);
        setSeries((t?.data?.series || []).map((d) => ({ ...d, date: String(d.run_date).slice(0, 10) })));
        setRows(r?.data?.data || []);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, effectiveCompanyId, startDate, endDate, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const brand = cssVar("--brand-600", 1) || cssVar("--brand", 1) || "#5affa2";
  const red = cssVar("--destructive", 1) || "hsl(0 84% 60%)";
  const blue = "hsl(217 91% 60%)";

  return (
    <div>
      <PageHeader
        title="Rerunner Recovery"
        subtitle="Decline-recovery performance for rerunner-enabled clients."
        meta={summary ? [
          { label: "Clients", value: fmtInt(summary.companies) },
          { label: "Days", value: fmtInt(summary.days) },
          { label: "Batches", value: fmtInt(summary.batches) },
        ] : []}
      />

      <div className="container py-6 space-y-6">
        {/* Controls */}
        <div className="surface rounded-xl ring-1 ring-border p-4 flex flex-col lg:flex-row lg:items-end gap-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Client</label>
            <select className="select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">All clients</option>
              {companies.map((c) => <option key={c.companyId} value={c.companyId}>{c.companyName || c.companyId}</option>)}
            </select>
          </div>
          <div className="lg:ml-auto">
            <label className="text-xs text-muted-foreground block mb-1">Range</label>
            <div className="inline-flex rounded-lg ring-1 ring-border overflow-hidden">
              {RANGE_PRESETS.map((p) => (
                <button key={p.days} onClick={() => setRangeDays(p.days)}
                  className={`px-3 py-2 text-sm ${rangeDays === p.days ? "bg-brand/15 text-foreground" : "text-muted-foreground hover:bg-muted/60"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="surface rounded-xl ring-1 ring-border p-8 text-center text-sm text-destructive">
            Failed to load rerunner analytics.
          </div>
        )}

        {/* KPI strip — raw numbers + PageKpis format (SOUND-410 lesson) */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="surface rounded-xl ring-1 ring-border p-5">
                <Skeleton.Rect className="h-5 w-20" /><Skeleton.Rect className="h-3 w-12 mt-2" />
              </div>
            ))}
          </div>
        ) : summary && (
          <PageKpis
            columns={{ base: 2, md: 3, lg: 5 }}
            items={[
              { label: "Declined Orders", value: Number(summary.declined) || 0, icon: <XCircle />, tone: "neutral" },
              { label: "Attempted", value: Number(summary.attempted) || 0, icon: <Repeat />, tone: "info" },
              { label: "Recovered", value: Number(summary.successful) || 0, icon: <CheckCircle />, tone: "success" },
              { label: "Recovery Rate", value: (Number(summary.recovery_rate) || 0) / 100, format: "percent", icon: <Repeat />, tone: "brand" },
              { label: "Failed", value: Number(summary.failed) || 0, icon: <XCircle />, tone: "destructive" },
            ]}
          />
        )}

        {/* Charts */}
        {!loading && series.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Recovery Rate" subtitle="Daily, recomputed from recovered / attempted">
              <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={36} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v) => [`${Number(v).toFixed(1)}%`, "Recovery"]} />
                <Line type="monotone" dataKey="recovery_rate" stroke={brand} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ChartCard>

            <ChartCard title="Reattempt Outcomes" subtitle="Recovered vs failed per day (= attempted)">
              <BarChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} width={44} tickFormatter={(v) => fmtInt(v)} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v, n) => [fmtInt(v), n === "successful" ? "Recovered" : "Failed"]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="successful" stackId="o" fill={brand} isAnimationActive={false} />
                <Bar dataKey="failed" stackId="o" fill={red} isAnimationActive={false} />
              </BarChart>
            </ChartCard>

            <ChartCard title="Decline → Attempt Funnel" subtitle="Declined orders vs reattempted per day">
              <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={(v) => fmtInt(v)} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v, n) => [fmtInt(v), n === "declined" ? "Declined" : "Attempted"]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="declined" stroke={blue} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="attempted" stroke={brand} strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ChartCard>

            <ChartCard title="Batches Run" subtitle="Rerunner batches per day">
              <BarChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={cssVar("--border", 0.5)} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} width={32} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle()} formatter={(v) => [fmtInt(v), "Batches"]} />
                <Bar dataKey="batches" fill={cssVar("--warning", 1) || "#f59e0b"} isAnimationActive={false} />
              </BarChart>
            </ChartCard>
          </div>
        )}

        {/* By-client leaderboard */}
        {!loading && !companyId && byCompany.length > 0 && (
          <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border"><h3 className="text-sm font-semibold text-foreground">By Client</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-card">
                  <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                    <th className="px-5 py-2">Client</th>
                    <th className="px-5 py-2 text-right">Declined</th>
                    <th className="px-5 py-2 text-right">Attempted</th>
                    <th className="px-5 py-2 text-right">Recovered</th>
                    <th className="px-5 py-2 text-right">Recovery Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {byCompany.map((c) => (
                    <tr key={c.company_id} className="hover:bg-muted/40 cursor-pointer" onClick={() => setCompanyId(c.company_id)}>
                      <td className="px-5 py-2 font-medium text-foreground">{c.company_name || c.company_id}</td>
                      <td className="px-5 py-2 text-right">{fmtInt(c.declined)}</td>
                      <td className="px-5 py-2 text-right">{fmtInt(c.attempted)}</td>
                      <td className="px-5 py-2 text-right">{fmtInt(c.successful)}</td>
                      <td className="px-5 py-2 text-right">{fmtPct(c.recovery_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detail grid */}
        {!loading && rows.length > 0 && (
          <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Detail (client × day)</h3>
              <span className="text-xs text-muted-foreground">{fmtInt(rows.length)} rows</span>
            </div>
            <div className="overflow-x-auto max-h-[560px]">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-card sticky top-0">
                  <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                    <th className="px-3 py-2">Run Date</th>
                    <th className="px-3 py-2">Client</th>
                    <th className="px-3 py-2 text-right">Batches</th>
                    <th className="px-3 py-2 text-right">Declined</th>
                    <th className="px-3 py-2 text-right">Filtered</th>
                    <th className="px-3 py-2 text-right">Already</th>
                    <th className="px-3 py-2 text-right">Attempted</th>
                    <th className="px-3 py-2 text-right">Recovered</th>
                    <th className="px-3 py-2 text-right">Failed</th>
                    <th className="px-3 py-2 text-right">Recovery %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r, i) => (
                    <tr key={`${r.company_id}|${r.run_date}|${i}`} className="hover:bg-muted/40">
                      <td className="px-3 py-2 text-muted-foreground">{String(r.run_date).slice(0, 10)}</td>
                      <td className="px-3 py-2">{r.company_name || r.company_id}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.batches_run)}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.total_declined_orders)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{fmtInt(r.total_filtered_out)}</td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{fmtInt(r.total_already_attempted)}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.total_attempted)}</td>
                      <td className="px-3 py-2 text-right text-green-500">{fmtInt(r.total_successful)}</td>
                      <td className="px-3 py-2 text-right text-destructive">{fmtInt(r.total_failed)}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtPct(r.daily_recovery_percentage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && series.length === 0 && (
          <div className="surface rounded-xl ring-1 ring-border p-12 text-center">
            <Layers className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No rerunner activity for the selected range.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(RerunnerRecoveryInner);
